import React, { FC } from 'react'
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useParams,
} from 'react-router-dom'
import { Location } from 'history'

// Components
import {
  DetailsViewRouteProps,
  DetailViewQuery,
} from 'modules/shares/DetailView'
import DocumentView from 'modules/shares/DocumentView'
import PageCanvasView from 'modules/shares/PageCanvasView'
import {
  extractVersionInformation,
  ShareVersionProvider,
  useVersioning,
} from 'modules/versioning'
import {
  getAllTemplatesForVersionedRoute,
  routes,
  RouteParams,
  RouteProps,
  NotFoundView,
  CWV_ROUTES,
  DocumentSidebarLayout,
} from '@sketch/modules-common'
import { ShareAuthorizerQueryData } from './useShareAuthorizerQuery'
import ShareSidebarTabContext from '../components/ShareSidebarTabContext'

// Hooks
import { useToast } from '@sketch/toasts'

// Utils
import AnnotationRedirect from './AnnotationRedirect'
import ArtboardUuidRedirect from './ArtboardUuidRedirect'
import { getToast } from 'modules/projects/toasts'
import { useOnEvent, ContainerRefScrollProvider } from '@sketch/utils'
import { parse } from 'query-string'
import { useHandleSettingsQueryParamIfNeeded } from '../DocumentView/DocumentView.hooks'
import { AnnotationSubscriptions } from 'modules/annotations/containers'
import { PrototypeViewFFGate } from 'modules/prototype/PrototypeViewFFGate'
import { PrototypeViewNextFFGate } from 'modules/prototype/PrototypeViewNextFFGate'
import { useUnfollowShareFromEmail } from '../operations'
import { PrototypeArtboardShortIdToUUIDRedirect } from './PrototypeArtboardShortIdToUUIDRedirect'

interface ShareMainRoutesProps
  extends Pick<ShareAuthorizerQueryData, 'share' | 'refetch'> {
  location: Location<any>
}

const ConnectedAnnotationSubscriptions = () => {
  const { versionIdentifier, share } = useVersioning()

  return (
    <AnnotationSubscriptions
      shareIdentifier={share.identifier}
      versionIdentifier={versionIdentifier || ''}
    />
  )
}

const UnsubscribeShareNotifications = () => {
  const { versionShortId, share } = useVersioning()

  // Run the unsubscribe hook
  useUnfollowShareFromEmail({
    versionShortId,
    shareIdentifier: share.identifier,
    shareName: share.name,
    subscriptionStatus: share.subscriptionStatus,
  })

  // Remove the "/unsubscribe from the URL"
  return (
    <Redirect to={routes.SHARE_VIEW.create({ shareID: share.identifier })} />
  )
}

const ShareMainRoutes: FC<ShareMainRoutesProps> = props => {
  const { share, refetch, location } = props

  const history = useHistory()
  const { showToast } = useToast()

  const { shareID } = useParams<RouteParams<'SHARE_VIEW'>>()
  const { version, ...shareBase } = share

  const { versionShortId } = extractVersionInformation(location.pathname)
  const latestVersion = version?.shortId!

  // Handle potential deep-link to settings modal
  useHandleSettingsQueryParamIfNeeded(share, {
    current: { shortId: versionShortId || latestVersion },
    latest: { shortId: latestVersion },
  })

  useOnEvent('projectAccessRevoked', projectData => {
    if (!share.project) {
      return
    }

    if (share.project.identifier === projectData.projectIdentifier) {
      history.push(
        routes.WORKSPACE_SHARES.create({
          workspaceId: projectData.workspaceIdentifier,
        })
      )

      showToast(
        getToast('PROJECT_ACCESS_REVOKED', projectData.projectName),
        'default'
      )
    }
  })

  return (
    <ShareSidebarTabContext>
      <ShareVersionProvider
        latestVersion={version!}
        share={shareBase}
        onVersionUpdateNeeded={async () => {
          await refetch()
        }}
      >
        <ConnectedAnnotationSubscriptions />
        <ContainerRefScrollProvider>
          <Switch>
            {/* Legacy prototype route redirect */}
            <Route
              exact
              path={getAllTemplatesForVersionedRoute('PROTOTYPE')}
              component={PrototypeArtboardShortIdToUUIDRedirect}
            />
            {/* Legacy prototype view using html player, manifest files and Artboard UUIDs */}
            <Route
              exact
              path={getAllTemplatesForVersionedRoute('PROTOTYPE_VIEW')}
              component={PrototypeViewFFGate}
            />
            {/* New prototype view for the WebGL player */}
            <Route
              exact
              path={getAllTemplatesForVersionedRoute('PROTOTYPE_VIEW_NEXT')}
              component={PrototypeViewNextFFGate}
            />
            {/* Artboard detail view based on UUID (redirect to ARTBOARD_DETAIL) */}
            <Route
              exact
              path={[
                ...getAllTemplatesForVersionedRoute('ARTBOARD_DETAIL_UUID'),
              ]}
              component={ArtboardUuidRedirect}
            />

            {/* Artboard detail view */}
            <Route
              exact
              path={[
                ...getAllTemplatesForVersionedRoute('ARTBOARD_REVISION'),
                ...getAllTemplatesForVersionedRoute('ARTBOARD_DETAIL'),
              ]}
              render={routeProps => (
                <DocumentSidebarLayout
                  render={layoutProps => (
                    <DetailViewQuery
                      {...(routeProps as DetailsViewRouteProps)}
                      layoutProps={layoutProps}
                    />
                  )}
                />
              )}
            />

            {/* Redirect to the appropriate ARTBOARD_DETAIL or SHARE_PAGE_CANVAS_VIEW for the given annotation */}
            <Route
              exact
              path={routes.ANNOTATION_REDIRECT.template()}
              component={AnnotationRedirect}
            />

            <Route
              exact
              path={[
                ...getAllTemplatesForVersionedRoute('SHARE_VIEW'),
                routes.SHARE_UNSUBSCRIBE.template(),
              ]}
              render={props => {
                const { location } = props
                const { search } = parse(location.search)

                /**
                 * If there's a search parameter
                 * we should render to the document view since it needs
                 * the grid view.
                 *
                 * Otherwise we show the canvas view
                 */
                const view = search ? (
                  <DocumentView />
                ) : (
                  <PageCanvasView {...(props as RouteProps<'SHARE_VIEW'>)} />
                )

                /**
                 * This route is mounted when the share_view url includes "/unsubscribe".
                 * Having this implemented as a sibling of the view makes the share component
                 * mount correctly and do the pending queries in order to show the view while
                 * at the same time unsubscribing it.
                 *
                 * We could have used a hook inside the PageCanvasView to perform this operation, but
                 * this would make the unsubscribe logic tight to the view and not the router. Also breaking
                 * this functionality if we ever change components in the future
                 */
                const unsubscribe = (
                  <Route
                    path={routes.SHARE_UNSUBSCRIBE.template()}
                    component={UnsubscribeShareNotifications}
                    exact
                  />
                )

                return (
                  <>
                    {view}
                    {unsubscribe}
                  </>
                )
              }}
            />

            <Route
              exact
              path={getAllTemplatesForVersionedRoute('SHARE_PAGE_CANVAS_VIEW')}
              component={PageCanvasView}
            />

            <Route
              exact
              path={[
                ...getAllTemplatesForVersionedRoute('SHARE_PAGE_VIEW'),
                ...getAllTemplatesForVersionedRoute('SHARE_PROTOTYPES'),
                ...CWV_ROUTES,
              ]}
              component={DocumentView}
            />

            {/* Catch the rest of the urls, redirect unknown routes to the root
          of the share. Have in mind that urls with 3 params like
          `s/:shareID/whatever` will be taken by LEGACY_ARTBOARD_DETAIL, and
           urls with 4 params will be taken by LEGACY_ARTBOARD_SLUG */}
            <Route path={routes.SHARE_VIEW.template()}>
              <Redirect to={routes.SHARE_VIEW.create({ shareID })} />
            </Route>
            {/* We should never reach this point, because of the previous redirection */}
            <Route component={NotFoundView} />
          </Switch>
        </ContainerRefScrollProvider>
      </ShareVersionProvider>
    </ShareSidebarTabContext>
  )
}

export default ShareMainRoutes
